/**
 * First-run walkthrough — the overlay.
 *
 * A short coach-mark tour shown once when the app is first opened (and replayable
 * from About → "Show the tour again"). It dims the screen, spotlights each real
 * control in turn — the MOB button, the menu, the favourites bar — and shows a
 * line of plain teaching copy beside it, with Next and Skip. The little state
 * machine and the copy live in walkthrough.ts; this component drives it and
 * paints it.
 *
 * Three things it's careful about:
 *  - It never blocks a real control. The dim layer is purely visual
 *    (pointer-events: none), so the MOB button underneath stays live the whole
 *    time; only the tour's own card takes taps.
 *  - It yields to safety. If any alarm is firing or a man-overboard is active,
 *    the tour hides itself and hands the screen back, then resumes once things
 *    are clear. It also won't start over the top of a live alarm.
 *  - It respects reduced motion — no pulse or slide when the OS asks for less.
 *
 * It's a plain shell overlay: it reads the target elements' positions from the
 * DOM by selector, so it doesn't need to touch the components it points at.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSettingsStore } from '@/store/settings';
import { useBootStore } from '@/store/boot';
import { useChartPlotterStore } from '@/store';
import { useAlarmsStore } from '@/store/alarms';
import { useSafetyStore } from '@/store/safety';
import { anyAlarmActive } from '@/safety';
import { useWalkthroughStore } from '@/store/walkthrough';
import { haptic } from '@/ui/haptics';
import {
  IDLE_TOUR,
  TOUR_STEP_COUNT,
  type TourState,
  type TourStep,
  currentStep,
  isLastStep,
  nextStep,
  shouldAutoStart,
  skipTour,
  startTour,
} from './walkthroughState';

/** Padding round a spotlit target, in px, so the ring sits just off the control. */
const SPOT_PAD = 8;
/** Wait this long after the map is ready before the tour appears, so the boot
 *  screen has faded and the first chart frame is settled underneath. */
const AUTOSTART_DELAY_MS = 1200;

/** Does the OS ask for reduced motion? Guarded for non-browser/test contexts. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Find the on-screen rect for a step's target, trying each selector in turn. */
function resolveRect(step: TourStep | null): DOMRect | null {
  if (!step || step.placement !== 'target' || !step.targets.length) return null;
  if (typeof document === 'undefined') return null;
  for (const sel of step.targets) {
    const el = document.querySelector(sel) as HTMLElement | null;
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return r;
  }
  return null;
}

export function Walkthrough() {
  const seen = useSettingsStore((s) => s.seenWalkthrough);
  const setSettings = useSettingsStore((s) => s.set);
  const mapReady = useBootStore((s) => s.mapReady);
  const hasFix = useChartPlotterStore((s) => s.ownship.position != null);
  const alarmsLive = useAlarmsStore((s) => s.live);
  const mob = useSafetyStore((s) => s.mob);
  const replayNonce = useWalkthroughStore((s) => s.replayNonce);

  const [tour, setTour] = useState<TourState>(IDLE_TOUR);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const reduceMotion = prefersReducedMotion();

  // Safety comes first: a live alarm or an active man-overboard means the tour
  // steps aside (it hides while this is true, then resumes).
  const safetyActive = anyAlarmActive(alarmsLive) || mob != null;

  // Auto-start once on a first launch, after the boot screen has cleared and only
  // when nothing safety-critical is on screen. A ref guards it to a single fire.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStarted.current) return;
    if (!shouldAutoStart(seen)) return;
    if (!mapReady || !hasFix || safetyActive) return;
    autoStarted.current = true;
    const t = setTimeout(() => setTour(startTour()), AUTOSTART_DELAY_MS);
    return () => clearTimeout(t);
  }, [seen, mapReady, hasFix, safetyActive]);

  // Replay when the About button asks (nonce bumps). Nonce 0 is the initial
  // value, so it never triggers on mount.
  const lastReplay = useRef(0);
  useEffect(() => {
    if (replayNonce === 0 || replayNonce === lastReplay.current) return;
    lastReplay.current = replayNonce;
    setTour(startTour());
  }, [replayNonce]);

  // Remember once the tour has ended, so we never auto-nag again.
  useEffect(() => {
    if (tour.completed && !seen) setSettings({ seenWalkthrough: true });
  }, [tour.completed, seen, setSettings]);

  const step = currentStep(tour);

  // Measure the current target. Re-measure on step change and on any resize or
  // scroll, plus a couple of animation frames after a step change to catch layout
  // settling (e.g. a control that's just appeared).
  const measure = useCallback(() => {
    setRect(resolveRect(step));
  }, [step]);

  useLayoutEffect(() => {
    if (!tour.running) return;
    measure();
    const r1 = requestAnimationFrame(measure);
    const r2 = requestAnimationFrame(() => requestAnimationFrame(measure));
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [tour.running, tour.step, measure]);

  const onNext = () => {
    haptic('select');
    setTour((s) => nextStep(s));
  };
  const onSkip = () => {
    haptic('tick');
    setTour((s) => skipTour(s));
  };

  if (!tour.running || !step) return null;
  // Yield the screen to a live alarm / MOB — keep our state, just don't paint.
  if (safetyActive) return null;

  const last = isLastStep(tour);
  const spotlit = step.placement === 'target' && rect != null;

  // The spotlight box (a transparent window with a huge dim shadow around it) is
  // positioned over the target. The card tucks above or below it depending on
  // which half of the screen the target sits in. Centred steps skip the spotlight
  // and place the card in the middle.
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  let cardPos: React.CSSProperties = {};
  if (spotlit && rect) {
    const below = rect.top < vh / 2;
    cardPos = below
      ? { top: rect.bottom + SPOT_PAD + 14, left: 0, right: 0 }
      : { bottom: vh - rect.top + SPOT_PAD + 14, left: 0, right: 0 };
  }

  const cls = [
    'acp-tour',
    reduceMotion ? 'no-motion' : '',
    spotlit ? 'spotlit' : 'centred',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cls}
      role="dialog"
      aria-modal="false"
      aria-label={`App tour, step ${tour.step + 1} of ${TOUR_STEP_COUNT}`}
    >
      {spotlit && rect ? (
        <div
          className="acp-tour-spot"
          aria-hidden="true"
          style={{
            top: rect.top - SPOT_PAD,
            left: rect.left - SPOT_PAD,
            width: rect.width + SPOT_PAD * 2,
            height: rect.height + SPOT_PAD * 2,
          }}
        />
      ) : (
        <div className="acp-tour-dim" aria-hidden="true" />
      )}

      <div className={`acp-tour-card${spotlit ? ' anchored' : ' centre'}`} style={cardPos}>
        <div className="acp-tour-copy">{step.copy}</div>
        <div className="acp-tour-foot">
          <div className="acp-tour-dots" aria-hidden="true">
            {Array.from({ length: TOUR_STEP_COUNT }).map((_, i) => (
              <span key={i} className={`acp-tour-dot${i === tour.step ? ' on' : ''}`} />
            ))}
          </div>
          <div className="acp-tour-btns">
            <button type="button" className="acp-tour-skip" onClick={onSkip}>
              {last ? 'Close' : 'Skip'}
            </button>
            <button type="button" className="acp-tour-next" onClick={onNext}>
              {last ? "Done, off you go" : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
